import React from "react";

const DocumentStatus = ({ status }) => {
  const classNames = {
    expired: "document-expired",
    critical: "document-critical",
    warning: "document-warning",
    valid: "document-valid",
    unknown: ""
  };
  
  return <span className={classNames[status.type]}>{status.label}</span>;
};

const DocumentsTable = ({ theme, title, documents, getDocumentStatus }) => {
  if (!documents || documents.length === 0) return null;
  
  return (
    <div className={`GM__${theme}-detail-section`}>
      <h3 className={`GM__${theme}-detail-section-title`}>
        {title}
      </h3>
      <div className={`GM__${theme}-documents-table`}>
        <div className={`GM__${theme}-documents-header`}>
          <div className={`GM__${theme}-document-name-header`}>
            Documento
          </div>
          <div className={`GM__${theme}-document-date-header`}>
            Fecha Exp.
          </div>
          <div className={`GM__${theme}-document-status-header`}>
            Estado
          </div>
        </div>

        {documents.map((doc) => (
          <div
            key={doc.id}
            className={`GM__${theme}-document-item`}
          >
            <div className={`GM__${theme}-document-name`}>
              {doc.document_type_name || "N/A"}
            </div>
            <div className={`GM__${theme}-document-date`}>
              {doc.expiration_date
                ? new Date(doc.expiration_date).toLocaleDateString()
                : "N/A"}
            </div>
            <div className={`GM__${theme}-document-status`}>
              <DocumentStatus status={getDocumentStatus(doc)} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const InfoSection = ({ theme, title, rows }) => (
  <div className={`GM__${theme}-detail-section`}>
    <h3 className={`GM__${theme}-detail-section-title`}>
      {title}
    </h3>
    <div className={`GM__${theme}-detail-grid`}>
      {rows.map((row, index) => (
        <div key={index} className={`GM__${theme}-detail-item`}>
          <span className={`GM__${theme}-detail-label`}>
            {row.label}:
          </span>
          <span className={`GM__${theme}-detail-value ${row.className || ''}`}>
            {row.value}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const VehicleDetails = ({ theme, vehicle, getDocumentStatus }) => {
  if (!vehicle) return null;
  
  // Determinar qué vehículo mostrar (regular o combinado)
  const vehicleData = vehicle.isCombined ? vehicle.vehicle : vehicle;
  
  const vehicleInfo = [
    { label: "Placa", value: vehicleData.plate || "N/A" },
    { label: "Tipo", value: vehicleData.vehicle_type_name || vehicleData.type || "N/A" },
    { label: "Marca", value: vehicleData.brand_name || vehicle.brand_name || "N/A" },
    { label: "Modelo", value: vehicleData.model || "N/A" },
    { label: "Año", value: vehicleData.year || "N/A" },
    { label: "Color", value: vehicleData.color || "N/A" },
    { label: "Teléfono", value: vehicleData.primary_phone_number ? 
      `${vehicleData.phone_country_code ? `+${vehicleData.phone_country_code} ` : ''}${vehicleData.primary_phone_number}` : 
      "N/A" },
    { 
      label: "Estado", 
      value: vehicleData.vehicle_active ? "Activo" : "Inactivo",
      className: vehicleData.vehicle_active ? "active" : "inactive"
    }
  ];
  
  // Si es combinado y tiene trailer
  if (vehicle.isCombined && vehicle.trailer) {
    vehicleInfo.push(
      { label: "Remolque", value: vehicle.trailer.plate || "N/A" },
      { label: "Tipo remolque", value: vehicle.trailer.type || "N/A" }
    );
  }
  
  // Información del propietario
  const ownerInfo = vehicle.isCombined ? 
    (vehicle.owner && vehicle.owner.id ? [
      { label: "Nombre", value: vehicle.owner.name || "N/A" },
      { label: "Teléfono", value: vehicle.owner.phone || "N/A" }
    ] : null) : 
    (vehicle.owner_id ? [
      { label: "Nombre", value: `${vehicle.owner_first_name || ''} ${vehicle.owner_last_name || ''}`.trim() || "N/A" },
      { label: "Teléfono", value: vehicle.owner_phone || "N/A" }
    ] : null);
  
  // Información del operador
  const operatorInfo = vehicle.isCombined ?
    (vehicle.operator && vehicle.operator.id ? [
      { label: "Nombre", value: vehicle.operator.name || "N/A" },
      { label: "Teléfono", value: vehicle.operator.phone || "N/A" }
    ] : null) :
    (vehicle.operator_id ? [
      { label: "Nombre", value: `${vehicle.operator_first_name || ''} ${vehicle.operator_last_name || ''}`.trim() || "N/A" },
      { label: "Teléfono", value: vehicle.operator_phone || "N/A" }
    ] : null);
  
  // Documentos del vehículo y operador
  const vehicleDocuments = vehicle.isCombined ? vehicle.vehicle.documents : vehicle.vehicleDocuments;
  const trailerDocuments = vehicle.isCombined ? vehicle.trailerDocuments : null;
  const operatorDocuments = vehicle.isCombined ? 
    (vehicle.operator ? vehicle.operator.documents : []) : 
    vehicle.operatorDocuments;
  
  return (
    <div className={`GM__${theme}-vehicle-details`}>
      {/* Información básica del vehículo */}
      <InfoSection 
        theme={theme}
        title="Información del Vehículo"
        rows={vehicleInfo}
      />

      {/* Información del propietario */}
      {ownerInfo && (
        <InfoSection 
          theme={theme}
          title="Propietario"
          rows={ownerInfo}
        />
      )}

      {/* Información del operador */}
      {operatorInfo && (
        <InfoSection 
          theme={theme}
          title="Operador"
          rows={operatorInfo}
        />
      )}

      {/* Documentos del vehículo */}
      <DocumentsTable 
        theme={theme}
        title="Documentos del Vehículo"
        documents={vehicleDocuments}
        getDocumentStatus={getDocumentStatus}
      />

      {/* Documentos del remolque (si aplica) */}
      {trailerDocuments && trailerDocuments.length > 0 && (
        <DocumentsTable 
          theme={theme}
          title="Documentos del Remolque"
          documents={trailerDocuments}
          getDocumentStatus={getDocumentStatus}
        />
      )}

      {/* Documentos del operador */}
      {operatorDocuments && operatorDocuments.length > 0 && (
        <DocumentsTable 
          theme={theme}
          title="Documentos del Operador"
          documents={operatorDocuments}
          getDocumentStatus={getDocumentStatus}
        />
      )}
    </div>
  );
};

export default React.memo(VehicleDetails);